import React from 'react';
import { Link } from 'react-router-dom';
import * as Fi from 'react-icons/fi';

type IconProps = React.SVGAttributes<SVGElement>;
const FiMaximize2 = Fi.FiMaximize2 as React.FC<IconProps>;
const FiDownload = Fi.FiDownload as React.FC<IconProps>;
const FiPrinter = Fi.FiPrinter as React.FC<IconProps>;
const FiSearch = Fi.FiSearch as React.FC<IconProps>;
const FiMinimize2 = Fi.FiMinimize2 as React.FC<IconProps>;
const FiFileText = Fi.FiFileText as React.FC<IconProps>;
const FiEdit2 = Fi.FiEdit2 as React.FC<IconProps>;
const FiTrash2 = Fi.FiTrash2 as React.FC<IconProps>;
const FiArrowLeft = Fi.FiArrowLeft as React.FC<IconProps>;
const FiSidebar = Fi.FiSidebar as React.FC<IconProps>;

export type FitMode = 'width' | 'page' | 'custom';

export interface DocumentViewerToolbarProps {
  onDownload: () => void;
  onPrint: () => void;
  onSearch?: () => void;
  searchActive?: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  documentName: string;
  /** Windows-style top actions: back, edit, delete */
  backHref?: string;
  editHref?: string;
  onDelete?: () => void;
  /** Right sidebar (ClickCompanion) open state and toggle */
  sidebarOpen?: boolean;
  onToggleSidebar?: () => void;
  /** Hide the download button (e.g. for Legal Forms / Forms & Precedents) */
  hideDownload?: boolean;
}

const DocumentViewerToolbar: React.FC<DocumentViewerToolbarProps> = ({
  onDownload,
  onPrint,
  onSearch,
  searchActive,
  isFullscreen,
  onToggleFullscreen,
  documentName,
  backHref,
  editHref,
  onDelete,
  sidebarOpen = true,
  onToggleSidebar,
  hideDownload = false,
}) => {
  const btn =
    'p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-900 dark:hover:text-slate-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent';
  const btnActive = 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 hover:bg-primary-150 dark:hover:bg-primary-800/50';

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap bg-white dark:bg-slate-800 px-4 py-2 shadow-sm min-h-[52px]">
      {/* Left: Windows-style actions (Back, Edit, Delete) + document title */}
      <div className="flex items-center gap-1 min-w-0">
        {backHref && (
          <Link
            to={backHref}
            className={btn}
            title="Back to list"
            aria-label="Back to list"
          >
            <FiArrowLeft className="w-5 h-5" />
          </Link>
        )}
        {editHref && (
          <Link
            to={editHref}
            className={btn}
            title="Edit document"
            aria-label="Edit document"
          >
            <FiEdit2 className="w-5 h-5" />
          </Link>
        )}
        {onDelete && (
          <button
            type="button"
            onClick={onDelete}
            className={`${btn} text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 dark:hover:text-red-300`}
            title="Delete"
            aria-label="Delete document"
          >
            <FiTrash2 className="w-5 h-5" />
          </button>
        )}
        {(backHref || editHref || onDelete) && (
          <div className="w-px h-6 bg-slate-200 dark:bg-slate-600 mx-0.5" />
        )}
        <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-200 min-w-0">
          <FiFileText className="shrink-0 w-5 h-5 text-slate-500 dark:text-slate-400" />
          <span className="font-medium truncate max-w-[200px] sm:max-w-[280px]" title={documentName}>
            {documentName}
          </span>
        </div>
      </div>

      {/* Right: sidebar toggle, search, download, print, fullscreen */}
      <div className="flex items-center gap-1">
        {onToggleSidebar && (
          <button
            type="button"
            onClick={onToggleSidebar}
            className={`${btn} ${!sidebarOpen ? btnActive : ''}`}
            title={sidebarOpen ? 'Hide companion panel' : 'Show companion panel'}
            aria-label={sidebarOpen ? 'Hide companion panel' : 'Show companion panel'}
          >
            <FiSidebar className="w-5 h-5" />
          </button>
        )}
        {onSearch && (
          <button
            type="button"
            onClick={onSearch}
            className={`${btn} ${searchActive ? btnActive : ''}`}
            title="Search in document"
          >
            <FiSearch className="w-5 h-5" />
          </button>
        )}
        {!hideDownload && (
          <button type="button" onClick={onDownload} className={btn} title="Download">
            <FiDownload className="w-5 h-5" />
          </button>
        )}
        {!hideDownload && (
          <button type="button" onClick={onPrint} className={btn} title="Print">
            <FiPrinter className="w-5 h-5" />
          </button>
        )}
        <div className="w-px h-6 bg-slate-200 dark:bg-slate-600" />
        <button
          type="button"
          onClick={onToggleFullscreen}
          className={btn}
          title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
        >
          {isFullscreen ? (
            <FiMinimize2 className="w-5 h-5" />
          ) : (
            <FiMaximize2 className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};

export default DocumentViewerToolbar;
